import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import Icon from "../../Icon";
import { useString as s } from "../../../components/StringProvider";
import { Paragraph } from "../../Typography";
import { themeProp } from "../../../theme";
import { useChat } from "./ChatProvider";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import actionTypes from "../../../store/actionTypes";
import { selectRequestData } from "../../../store/reducers";
import { getConversationPrompts } from "../../../store/actions/ai";

const ChatPrompt = ({ getConversationPrompts, promptsData }) => {
  const promptHeading = s(
    "aiDiscoveryModal.chat.prompt.heading",
    "Select Data Sources, choose or create a prompt, and you're ready to hit Send"
  );
  const { selectedPrompt, setSelectedPrompt, canEnterPrompt } = useChat();
  const chatPromptTopRef = useRef(null);

  const scrollToTop = () => {
    chatPromptTopRef.current?.scrollIntoView({ behavior: "instant" });
  };

  useEffect(() => {
    getConversationPrompts();
    scrollToTop();
  }, []);

  return (
    <ChatPromptContainer data-cy={"chat-prompt"}>
      <ChatPromptScrollTop ref={chatPromptTopRef} />
      <ChatPromptHeader>
        <Icon name={"aiPrompt"} colour={"primary"} size={"xlarge"} />
        <Paragraph variant={"bMedium"}>{promptHeading}</Paragraph>
      </ChatPromptHeader>
      <ChatPromptLine />
      <ChatPromptButtonContainer>
        {promptsData?.map((item, index) => (
          <ChatPromptButton
            key={index}
            $active={selectedPrompt?.name === item.name}
            onClick={() => setSelectedPrompt(item)}
            disabled={!canEnterPrompt}
            data-cy={`chat-prompt-button-${index}`}
            className={
              selectedPrompt?.name === item.name ? "active" : "inactive"
            }
          >
            <Icon name={"work"} colour={"primary"} size={"medium"} />
            <Paragraph variant={"body"}>{item.name}</Paragraph>
          </ChatPromptButton>
        ))}
      </ChatPromptButtonContainer>
    </ChatPromptContainer>
  );
};

const ChatPromptContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ChatPromptHeader = styled.div`
  max-width: 472px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const ChatPromptScrollTop = styled.div``;

const ChatPromptLine = styled.hr`
  width: 40px;
  margin: 24px 0px;
  border: 1px solid ${themeProp("palette.gray2")};
`;

const ChatPromptButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChatPromptButton = styled.button`
  display: flex;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 6px;
  border-style: none;
  border: 1px solid ${themeProp("palette.gray2")};
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.03),
    0px 1px 6px -1px rgba(0, 0, 0, 0.02), 0px 2px 4px 0px rgba(0, 0, 0, 0.02);
  background-color: ${({ $active }) =>
    $active ? themeProp("palette.secondary") : themeProp("palette.surface")};
  color: ${({ $active }) =>
    $active ? themeProp("palette.surface") : themeProp("palette.text")};
  font-weight: ${({ $active }) =>
    $active
      ? themeProp("typography.h5Small.fontWeight")
      : themeProp("typography.body.fontWeight")};
  font-family: ${themeProp("typography.body.fontFamily")};
  cursor: pointer;
  text-align: left;

  .ant-typography {
    color: ${({ $active }) =>
      $active ? themeProp("palette.surface") : themeProp("palette.text")};
  }

  svg {
    color: ${({ $active }) =>
      $active ? themeProp("palette.surface") : themeProp("palette.primary")};
    font-weight: ${({ $active }) =>
      $active
        ? themeProp("typography.h5Small.fontWeight")
        : themeProp("typography.body.fontWeight")};
    margin-right: 12px;
    margin-top: 3px;
  }

  &:hover {
    .ant-typography {
      color: ${({ $active }) =>
        $active
          ? themeProp("palette.surface")
          : themeProp("palette.secondary")};
    }

    svg {
      color: ${({ $active }) =>
        $active
          ? themeProp("palette.surface")
          : themeProp("palette.secondary")};
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const mapStateToProps = (state) => ({
  promptsData: selectRequestData(
    state,
    actionTypes.GET_CONVERSATION_PROMPTS_REQUEST
  )
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getConversationPrompts }, dispatch);

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ChatPrompt
);
